import { useFormContext } from "react-hook-form";
import { RatingFieldProps } from "./Rating.types";
import { FormControl, FormDescription, FormField, FormItem, FormMessage } from "@shadcn/components/ui/form";
import { Label } from "@shadcn/components/ui/label";
import { getValidationConfig } from "@forms/form-render/helpers/getValidationConfig";
import { getFieldHelperText } from "@forms/form-render/helpers/getFieldHelperText";
import { Star } from "lucide-react";
import { cn } from "@shadcn/lib/utils";

export const Rating = ({ label, name, required, validation, config, disabled }: RatingFieldProps) => {
  const { control } = useFormContext();
  const description = getFieldHelperText(config.helperText, required);
  const validationConfig = getValidationConfig(validation);
  const maxStars = config.maxStars || 5;
  const defaultValue = config.defaultValue ?? 0;

  return (
    <FormField
      control={control}
      defaultValue={defaultValue}
      rules={{
        ...validationConfig,
        required: {
          value: required,
          message: "This is a required field",
        },
        min: {
          value: required ? 1 : 0,
          message: required ? "Please select at least 1 star" : "Rating must be at least 0",
        },
        max: {
          value: maxStars,
          message: `Rating cannot exceed ${maxStars} stars`,
        },
      }}
      name={name}
      render={({ field }) => (
        <FormItem className="space-y-2">
          {label && <Label htmlFor={name}>{label}</Label>}
          <FormMessage />
          <FormControl>
            <div className="flex items-center gap-1" role="radiogroup" aria-label={label || "Rating"}>
              {Array.from({ length: maxStars }, (_, index) => {
                const starValue = index + 1;
                const currentValue = field.value ?? 0;
                const isSelected = currentValue >= starValue;
                return (
                  <button
                    key={starValue}
                    type="button"
                    onClick={() => {
                      if (!disabled) {
                        const currentValue = field.value ?? 0;
                        const newValue = currentValue === starValue ? 0 : starValue;
                        field.onChange(newValue);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (!disabled) {
                          const currentValue = field.value ?? 0;
                          const newValue = currentValue === starValue ? 0 : starValue;
                          field.onChange(newValue);
                        }
                      } else if (e.key === "ArrowRight" && starValue < maxStars) {
                        e.preventDefault();
                        if (!disabled) {
                          const currentValue = field.value ?? 0;
                          field.onChange(Math.min(currentValue + 1, maxStars));
                        }
                      } else if (e.key === "ArrowLeft" && starValue > 1) {
                        e.preventDefault();
                        if (!disabled) {
                          const currentValue = field.value ?? 0;
                          field.onChange(Math.max(currentValue - 1, 0));
                        }
                      }
                    }}
                    disabled={disabled}
                    className={cn(
                      "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary rounded transition-colors",
                      disabled && "cursor-not-allowed opacity-50"
                    )}
                    aria-label={`${starValue} star${starValue !== 1 ? "s" : ""}`}
                    aria-pressed={isSelected}
                    tabIndex={disabled ? -1 : 0}
                  >
                    <Star
                      className={cn(
                        "h-6 w-6 transition-colors",
                        isSelected
                          ? "fill-yellow-400 text-yellow-400"
                          : "fill-gray-200 text-gray-300 hover:fill-yellow-200 hover:text-yellow-200"
                      )}
                    />
                  </button>
                );
              })}
              <input type="hidden" {...field} value={field.value ?? 0} />
            </div>
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
        </FormItem>
      )}
    />
  );
};

